<template>
  <div class="campaign-status-page">
    <div class="campaign-status-card">
      <div class="campaign-status-header">
        <div class="campaign-status-title-wrapper">
          <div class="campaign-status-icon">
            <Icons
              name="CampaignStatusIcon"
              size="lg"
            />
          </div>
          <span class="campaign-status-title-text">Kampanya Durumu</span>
        </div>

        <div class="campaign-status-badge-wrapper">
          <CampaignStatusBadge
            :status="status"
            :definitions="campaignDefinitions"
          />
          <div
            v-if="
              status === JOINED_STATUS_TYPES.PLANNED && statusData.joinDate
            "
            class="campaign-status-description"
          >
            <span class="semi-bold">
              {{ statusData.joinDate }}
            </span>
            <span>tarihinde başlayacak</span>
          </div>
        </div>
      </div>

      <div class="campaign-status-progress-container">
        <div
          v-if="showValidityBar"
          class="campaign-status-item"
        >
          <div class="campaign-status-item-header">
            <div class="campaign-status-label-wrapper">
              <span class="label">Geçerlilik Süresi (Gün)</span>
              <TooltipPlugin
                tooltip-text="Bu alan; kampanyaya katıldıktan sonra kampanya bitimine kadar geçen süreyi ve kampanyanın toplam geçerlilik süresini gösterir."
                :tooltip-width="315"
              >
                <template #tooltip-btn>
                  <Icons
                    name="InfoCircle"
                    color="#B4B4B4"
                    size="sm"
                    class="tooltip-icon"
                  />
                </template>
              </TooltipPlugin>
            </div>
            <span class="value">{{ statusData.validityUsed }} /
              {{ statusData.validityTotal }}</span>
          </div>
          <div class="campaign-status-progress">
            <ProgressBar
              :rectangle-value="statusData.dateUsagePercentage"
              :rectangle-size="'large'"
              show-percentage-on-zero="true"
            />
          </div>
        </div>

        <div
          v-if="isGmvLimitUsed"
          class="campaign-status-item"
        >
          <div class="campaign-status-item-header">
            <div class="campaign-status-label-wrapper">
              <span class="label">Kampanya Toplam Satış Limiti (₺)</span>
              <TooltipPlugin
                tooltip-text="Bu alan; kampanya kapsamında satılan ürünlerin toplam satışını ve kampanyanın limitini gösterir. İndirim ve komisyon tutarları çıkarılmamıştır."
                :tooltip-width="315"
              >
                <template #tooltip-btn>
                  <Icons
                    name="InfoCircle"
                    color="#B4B4B4"
                    size="sm"
                    class="tooltip-icon"
                  />
                </template>
              </TooltipPlugin>
            </div>
            <span class="value">{{ statusData.salesUsed }} / {{ statusData.salesTotal }}</span>
          </div>
          <div class="campaign-status-progress">
            <ProgressBar
              :rectangle-value="statusData.salesPercent"
              :rectangle-size="'large'"
              show-percentage-on-zero="true"
            />
          </div>
        </div>

        <div
          v-if="isOrderLimitUsed"
          class="campaign-status-item"
        >
          <div class="campaign-status-item-header">
            <div class="campaign-status-label-wrapper">
              <span class="label">Kampanya Sipariş Limiti (Adet)</span>
              <TooltipPlugin
                tooltip-text="Bu alan; kampanya süresince alınan toplam sipariş adedini ve kampanyanın limitini gösterir."
                :tooltip-width="315"
              >
                <template #tooltip-btn>
                  <Icons
                    name="InfoCircle"
                    color="#B4B4B4"
                    size="sm"
                    class="tooltip-icon"
                  />
                </template>
              </TooltipPlugin>
            </div>
            <span class="value">{{ statusData.orderUsed }} / {{ statusData.orderTotal }}</span>
          </div>
          <div class="campaign-status-progress">
            <ProgressBar
              :rectangle-value="statusData.orderPercent"
              :rectangle-size="'large'"
              show-percentage-on-zero="true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icons, ProgressBar, TooltipPlugin } from '@n11/so-ui-components';
import { CampaignStatusBadge } from '.';
import { JOINED_STATUS_TYPES } from '../constants';

defineProps({
  status: {
    type: String,
    default: '',
  },
  campaignDefinitions: {
    type: Array,
    default: () => [],
  },
  statusData: {
    type: Object,
    required: true,
    default: () => ({
      validityUsed: '0',
      validityTotal: '0',
      validityPercent: 0,
      salesUsed: '0',
      salesTotal: '0',
      salesPercent: 0,
      orderUsed: '0',
      orderTotal: '0',
      orderPercent: 0,
      joinDate: '',
    }),
  },
  isGmvLimitUsed: {
    type: Boolean,
    default: true,
  },
  isOrderLimitUsed: {
    type: Boolean,
    default: true,
  },
  showValidityBar: {
    type: Boolean,
    default: true,
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.campaign-status-page {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  margin-bottom: 16px;
}

.campaign-status-page-header {
  display: flex;
  padding: 16px 0;
  align-items: center;
  gap: 10px;
  align-self: stretch;
}

.campaign-status-page-title {
  color: $colorN80;
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: -0.2px;
}

.campaign-status-card {
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 8px;
  background: $colorN00;
  align-self: stretch;
}

.campaign-status-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.campaign-status-title-wrapper {
  display: flex;
  gap: 4px;
  align-items: flex-start;
}

.campaign-status-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.campaign-status-title-text {
  color: $colorN80;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.1px;
}

.campaign-status-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.campaign-status-description {
  color: $colorY60;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Open Sans;
  font-weight: 400;
  font-style: Regular;
  font-size: 11px;
  line-height: 14px;
  vertical-align: middle;
}

.campaign-status-progress-container {
  display: flex;
  align-items: center;
  gap: 24px;
  align-self: stretch;
}

.campaign-status-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0 4px;

  :deep(.progress-container) {
    gap: 4px;
  }

  :deep(.progress) {
    gap: 8px;
    width: 100%;
  }

  :deep(.rectangle) {
    width: 100% !important;
    padding-right: 0 !important;
    background-color: $colorM05;
  }
}

.campaign-status-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .value {
    color: $colorN80;
    text-align: right;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }
}

.campaign-status-label-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;

  .label {
    color: $colorN60;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  .info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .tooltip-icon {
    cursor: pointer;
  }
}
</style>
